import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AnalyticsService, cn, isRecordWithProperties, logger } from "@openzeppelin/ui-utils";
import { jsx, jsxs } from "react/jsx-runtime";
import { QueryClient, useQuery } from "@tanstack/react-query";
import { Button } from "@openzeppelin/ui-components";

//#region src/version.ts
const VERSION = "3.3.2";

//#endregion
//#region src/hooks/AdapterContext.tsx
/**
* AdapterContext.tsx
*
* This file defines the React Context used for the runtime singleton pattern.
* It provides types and the context definition, but the actual implementation
* is in the `RuntimeProvider` component.
*
* The runtime singleton pattern ensures that only one runtime instance exists
* per network configuration, which is critical for consistent wallet connection
* state across the application.
*/
/**
* The React Context that provides runtime registry access throughout the app.
* Components can access this through the `useRuntimeContext` hook.
*/
const RuntimeContext = createContext(null);

//#endregion
//#region src/hooks/AdapterProvider.tsx
/**
* RuntimeProvider.tsx
*
* This file implements the Runtime Provider component which manages a registry of
* runtime instances. It's a key part of the runtime singleton pattern which ensures
* that only one runtime instance exists per network configuration.
*
* The runtime registry is shared across the application via React Context, allowing
* components to access the same runtime instances and maintain consistent wallet
* connection state.
*
* IMPORTANT: This implementation needs special care to avoid React state update errors
* during component rendering. Direct state updates during render are not allowed, which
* is why runtime loading is controlled carefully.
*/
/**
* Provider component that manages runtime instances centrally
* to avoid creating multiple instances of the same runtime.
*
* This component:
* 1. Maintains a registry of runtime instances by network ID
* 2. Tracks loading states for runtimes being initialized
* 3. Provides a function to get or load runtimes for specific networks
* 4. Ensures runtime instances are reused when possible
*/
function RuntimeProvider({ children, resolveRuntime }) {
	const [runtimeRegistry, setRuntimeRegistry] = useState({});
	const [loadingNetworks, setLoadingNetworks] = useState(/* @__PURE__ */ new Set());
	const runtimeRegistryRef = useRef(runtimeRegistry);
	const failedNetworksRef = useRef(/* @__PURE__ */ new Set());
	const resolverGenerationRef = useRef(0);
	const resolverInitializedRef = useRef(false);
	useEffect(() => {
		runtimeRegistryRef.current = runtimeRegistry;
	}, [runtimeRegistry]);
	useEffect(() => {
		if (resolverInitializedRef.current) resolverGenerationRef.current += 1;
		else resolverInitializedRef.current = true;
		const registry = runtimeRegistryRef.current;
		const runtimes = Object.values(registry);
		const hadCachedRuntimes = runtimes.length > 0;
		failedNetworksRef.current = /* @__PURE__ */ new Set();
		setLoadingNetworks(/* @__PURE__ */ new Set());
		if (hadCachedRuntimes) {
			setRuntimeRegistry({});
			setTimeout(() => {
				runtimes.forEach((runtime) => {
					try {
						runtime.dispose();
					} catch (error) {
						logger.error("RuntimeProvider", "Error disposing runtime during registry flush:", error);
					}
				});
			}, 0);
		}
	}, [resolveRuntime]);
	useEffect(() => {
		return () => {
			Object.values(runtimeRegistryRef.current).forEach((runtime) => {
				runtime.dispose();
			});
		};
	}, []);
	useEffect(() => {
		const runtimeCount = Object.keys(runtimeRegistry).length;
		if (runtimeCount > 0) logger.info("RuntimeProvider", `Registry contains ${runtimeCount} runtimes:`, {
			networkIds: Object.keys(runtimeRegistry),
			loadingCount: loadingNetworks.size,
			loadingNetworkIds: Array.from(loadingNetworks)
		});
	}, [runtimeRegistry, loadingNetworks]);
	/**
	* Evicts a runtime from the registry by network ID and disposes it.
	* This is the safe counterpart to `getRuntimeForNetwork`: callers should only
	* release a runtime after they have already promoted its replacement.
	*
	* Disposal is deferred to the next macrotask so React's commit phase
	* (including development-mode prop diffing) can finish reading the old
	* runtime's properties without hitting the RuntimeDisposedError proxy trap.
	*/
	const releaseRuntime = useCallback((networkId) => {
		const runtime = runtimeRegistryRef.current[networkId];
		if (!runtime) return;
		logger.info("RuntimeProvider", `Releasing runtime for network ${networkId}`);
		setRuntimeRegistry((prev) => {
			const next = { ...prev };
			delete next[networkId];
			return next;
		});
		setTimeout(() => {
			try {
				runtime.dispose();
			} catch (error) {
				logger.error("RuntimeProvider", `Error disposing runtime for network ${networkId}:`, error);
			}
		}, 0);
	}, []);
	/**
	* Function to get or create a runtime for a network
	*
	* IMPORTANT: Runtime loading is coordinated carefully to avoid React state updates
	* during render, which would cause errors.
	*
	* This function:
	* 1. Returns existing runtimes immediately if available
	* 2. Reports loading state for runtimes being initialized
	* 3. Initiates runtime loading when needed
	*/
	const getRuntimeForNetwork = useCallback((networkConfig) => {
		if (!networkConfig) return {
			runtime: null,
			isLoading: false
		};
		const networkId = networkConfig.id;
		logger.debug("RuntimeProvider", `Runtime requested for network ${networkId}`);
		if (runtimeRegistry[networkId]) {
			logger.debug("RuntimeProvider", `Using existing runtime for network ${networkId}`);
			return {
				runtime: runtimeRegistry[networkId],
				isLoading: false
			};
		}
		if (loadingNetworks.has(networkId)) {
			logger.debug("RuntimeProvider", `Runtime for network ${networkId} is currently loading`);
			return {
				runtime: null,
				isLoading: true
			};
		}
		if (failedNetworksRef.current.has(networkId)) {
			logger.debug("RuntimeProvider", `Runtime for network ${networkId} previously failed; skipping retry`);
			return {
				runtime: null,
				isLoading: false
			};
		}
		setLoadingNetworks((prev) => {
			const newSet = new Set(prev);
			newSet.add(networkId);
			return newSet;
		});
		logger.info("RuntimeProvider", `Starting runtime initialization for network ${networkId} (${networkConfig.name})`);
		const generation = resolverGenerationRef.current;
		resolveRuntime(networkConfig).then((runtime) => {
			if (generation !== resolverGenerationRef.current) {
				setTimeout(() => {
					try {
						runtime.dispose();
					} catch (error) {
						logger.error("RuntimeProvider", `Error disposing stale runtime for network ${networkId}:`, error);
					}
				}, 0);
				setLoadingNetworks((prev) => {
					const newSet = new Set(prev);
					newSet.delete(networkId);
					return newSet;
				});
				return;
			}
			logger.info("RuntimeProvider", `Runtime for network ${networkId} loaded successfully`, {
				type: runtime.constructor.name,
				objectId: Object.prototype.toString.call(runtime)
			});
			setRuntimeRegistry((prev) => ({
				...prev,
				[networkId]: runtime
			}));
			setLoadingNetworks((prev) => {
				const newSet = new Set(prev);
				newSet.delete(networkId);
				return newSet;
			});
		}).catch((error) => {
			if (generation !== resolverGenerationRef.current) {
				setLoadingNetworks((prev) => {
					const newSet = new Set(prev);
					newSet.delete(networkId);
					return newSet;
				});
				return;
			}
			logger.error("RuntimeProvider", `Error loading runtime for network ${networkId}:`, error);
			failedNetworksRef.current.add(networkId);
			setLoadingNetworks((prev) => {
				const newSet = new Set(prev);
				newSet.delete(networkId);
				return newSet;
			});
		});
		return {
			runtime: null,
			isLoading: true
		};
	}, [
		runtimeRegistry,
		loadingNetworks,
		resolveRuntime
	]);
	const contextValue = useMemo(() => ({
		getRuntimeForNetwork,
		releaseRuntime
	}), [getRuntimeForNetwork, releaseRuntime]);
	return /* @__PURE__ */ jsx(RuntimeContext.Provider, {
		value: contextValue,
		children
	});
}

//#endregion
//#region src/hooks/WalletStateContext.ts
/**
* Shared Global Context Pattern
* =============================
*
* WHY THIS EXISTS:
* When bundlers (like Vite's optimizeDeps with esbuild) pre-bundle dependencies,
* they may inline transitive dependencies like @openzeppelin/ui-react into the
* consuming package's bundle. This creates MULTIPLE instances of this module:
*
* 1. The app's direct import → packages/react/dist/index.js
* 2. The adapter package's inlined copy → .vite/deps/@openzeppelin_adapter_evm.js
*
* Since React contexts use referential identity, these two module instances have
* DIFFERENT context objects. When the adapter's components call useWalletState(),
* they look for a context that was never provided (because WalletStateProvider
* uses the app's context, not the adapter's inlined copy).
*
* SOLUTION:
* Store the context object on globalThis so ALL module instances share the same
* React context, regardless of how they were loaded or bundled.
*
* WHEN IS THIS NEEDED:
* - Development with Vite's dependency pre-bundling (optimizeDeps)
* - When adapters are installed from npm (not workspace-linked with same bundler)
* - Any scenario where @openzeppelin/ui-react might be duplicated
*
* PRODUCTION APPS:
* In production builds where the app and adapters are bundled together with proper
* deduplication (e.g., via bundler configuration or peer dependencies), this
* workaround may not be strictly necessary. However, it provides a safety net
* and has minimal overhead.
*/
/**
* Use Symbol.for() to create a globally unique key that is consistent across
* all module instances. Unlike Symbol(), Symbol.for() returns the same symbol
* for the same key string, which is essential for cross-module sharing.
*/
const WALLET_STATE_CONTEXT_KEY = Symbol.for("@openzeppelin/ui-react/WalletStateContext");
/**
* Retrieves or creates the shared WalletStateContext.
*
* NOTE ON ATOMICITY:
* The check-then-set pattern here is not atomic, but this is acceptable because:
* 1. JavaScript is single-threaded; module initialization is synchronous
* 2. Even if multiple modules initialize "simultaneously" during parallel loading,
*    they execute sequentially on the main thread
* 3. The worst case (two contexts created) would only happen if the check and set
*    were somehow interleaved, which cannot occur in JS's execution model
* 4. For React contexts specifically, having the same context object is what matters,
*    and this pattern guarantees that after initialization
*/
function getOrCreateSharedContext$1() {
	const global = globalThis;
	if (!global[WALLET_STATE_CONTEXT_KEY]) global[WALLET_STATE_CONTEXT_KEY] = createContext(void 0);
	return global[WALLET_STATE_CONTEXT_KEY];
}
const WalletStateContext = getOrCreateSharedContext$1();
/**
* Hook to access wallet state from WalletStateProvider.
* @throws Error if used outside of WalletStateProvider
*/
function useWalletState() {
	const context = React.useContext(WalletStateContext);
	if (context === void 0) throw new Error("useWalletState must be used within a WalletStateProvider");
	return context;
}

//#endregion
//#region src/hooks/useAdapterContext.ts
/**
* useAdapterContext.ts
*
* This file provides a hook to access the runtime context throughout the application.
* It's a critical part of the runtime singleton pattern, allowing components to
* access the centralized runtime registry.
*
* The runtime singleton pattern ensures:
* - Only one runtime instance exists per network
* - Wallet connection state is consistent across the app
* - Better performance by eliminating redundant runtime initialization
*/
/**
* Hook to access the runtime context
*
* This hook provides access to the `getRuntimeForNetwork` function which
* retrieves or creates runtime instances from the singleton registry.
*
* Components should typically use the higher-level wallet/runtime hooks instead
* of this hook directly, as it handles React state update timing properly.
*
* @throws Error if used outside of a RuntimeProvider context
* @returns The runtime context value
*/
function useRuntimeContext() {
	const context = useContext(RuntimeContext);
	if (!context) throw new Error("useRuntimeContext must be used within a RuntimeProvider");
	return context;
}

//#endregion
//#region src/hooks/walletSessionRegistry.ts
/**
* Returns the cached wallet session for an ecosystem, if present.
*
* @param registry - Current internal wallet session registry.
* @param ecosystem - Ecosystem key to resolve.
* @returns The cached session entry or `null` when the ecosystem has not been configured yet.
*/
function getWalletSession(registry, ecosystem) {
	if (!ecosystem) return null;
	return registry[ecosystem] ?? null;
}
/**
* Inserts or replaces the cached wallet session for a single ecosystem.
*
* @param registry - Current internal wallet session registry.
* @param session - Session entry to cache.
* @returns A new registry object containing the upserted ecosystem session.
*/
function upsertWalletSession(registry, session) {
	return {
		...registry,
		[session.ecosystem]: session
	};
}

//#endregion
//#region src/hooks/WalletStateProvider.tsx
/**
* Configures the runtime's UI kit capability and returns the ecosystem session artifacts.
*/
async function configureRuntimeUiKit(runtime, loadConfigModule, programmaticOverrides = {}) {
	const uiKit = runtime.uiKit;
	if (!uiKit) return {
		providerComponent: null,
		hooks: null
	};
	try {
		const hasUiKitOverride = Object.keys(programmaticOverrides).length > 0;
		if (typeof uiKit.configureUiKit === "function") {
			const nextUiKitConfig = { ...programmaticOverrides };
			logger.info("[WSP configureRuntimeUiKit]", hasUiKitOverride ? `Applying explicit UI kit overrides for runtime: ${runtime.networkConfig.id}` : `Initializing runtime UI kit from adapter/app defaults for runtime: ${runtime.networkConfig.id}`);
			await uiKit.configureUiKit(nextUiKitConfig, { loadUiKitNativeConfig: loadConfigModule });
			logger.info("[WSP configureRuntimeUiKit] configureUiKit completed for runtime:", runtime.networkConfig.id);
		}
		const providerComponent = uiKit.getEcosystemReactUiContextProvider?.() || null;
		const hooks = uiKit.getEcosystemReactHooks?.() || null;
		logger.info("[WSP configureRuntimeUiKit]", "UI provider and hooks retrieved successfully.");
		return {
			providerComponent,
			hooks
		};
	} catch (error) {
		logger.error("[WSP configureRuntimeUiKit]", "Error during runtime UI setup:", error);
		throw error;
	}
}
/**
* @name WalletStateProvider
* @description This provider is a central piece of the application's state management for wallet and network interactions.
* It is responsible for:
* 1. Managing the globally selected active network ID (`activeNetworkId`).
* 2. Deriving the full `NetworkConfig` object (`activeNetworkConfig`) for the active network.
* 3. Fetching and providing the corresponding `EcosystemRuntime` (`activeRuntime`) for the active network,
*    leveraging the `RuntimeProvider` to ensure runtime singletons.
* 4. Caching ecosystem-scoped wallet session artifacts (provider roots and facade hooks)
*    independently from the network-scoped runtime.
* 5. Rendering the active ecosystem wallet provider (e.g., WagmiProvider for EVM) around its
*    children, which is essential for the facade hooks to function correctly.
* 6. Providing a function (`setActiveNetworkId`) to change the globally active network.
*
* Consumers use the `useWalletState()` hook to access this global state.
* It should be placed high in the component tree, inside a `<RuntimeProvider>`.
*/
function WalletStateProvider({ children, initialNetworkId = null, getNetworkConfigById, loadConfigModule }) {
	const [currentGlobalNetworkId, setCurrentGlobalNetworkIdState] = useState(initialNetworkId);
	const [currentGlobalNetworkConfig, setCurrentGlobalNetworkConfig] = useState(null);
	const [globalActiveRuntime, setGlobalActiveRuntime] = useState(null);
	const [isGlobalRuntimeLoading, setIsGlobalRuntimeLoading] = useState(false);
	const [walletSessionRegistry, setWalletSessionRegistry] = useState({});
	const [activeWalletSessionEcosystem, setActiveWalletSessionEcosystem] = useState(null);
	const [uiKitConfigVersion, setUiKitConfigVersion] = useState(0);
	const [programmaticUiKitConfig, setProgrammaticUiKitConfig] = useState(void 0);
	const { getRuntimeForNetwork, releaseRuntime } = useRuntimeContext();
	const promotedNetworkIdRef = useRef(null);
	useEffect(() => {
		const abortController = new AbortController();
		async function fetchNetworkConfig() {
			if (!currentGlobalNetworkId) {
				if (!abortController.signal.aborted) setCurrentGlobalNetworkConfig(null);
				return;
			}
			try {
				const config = await Promise.resolve(getNetworkConfigById(currentGlobalNetworkId));
				if (!abortController.signal.aborted) setCurrentGlobalNetworkConfig(config || null);
			} catch (error) {
				if (!abortController.signal.aborted) {
					logger.error("[WSP fetchNetworkConfig]", "Failed to fetch network config:", error);
					setCurrentGlobalNetworkConfig(null);
				}
			}
		}
		fetchNetworkConfig();
		return () => abortController.abort();
	}, [currentGlobalNetworkId, getNetworkConfigById]);
	useEffect(() => {
		const abortController = new AbortController();
		async function loadRuntimeAndConfigureUi() {
			if (!currentGlobalNetworkConfig) {
				if (!abortController.signal.aborted) {
					const prevNetworkId = promotedNetworkIdRef.current;
					setGlobalActiveRuntime(null);
					setIsGlobalRuntimeLoading(false);
					setActiveWalletSessionEcosystem(null);
					if (prevNetworkId) {
						releaseRuntime(prevNetworkId);
						promotedNetworkIdRef.current = null;
					}
				}
				return;
			}
			const { runtime: newRuntime, isLoading: newIsLoading } = getRuntimeForNetwork(currentGlobalNetworkConfig);
			if (abortController.signal.aborted) return;
			setIsGlobalRuntimeLoading(newIsLoading);
			if (newRuntime && !newIsLoading) try {
				const { providerComponent, hooks } = await configureRuntimeUiKit(newRuntime, loadConfigModule, programmaticUiKitConfig);
				if (!abortController.signal.aborted) {
					const ecosystem = newRuntime.networkConfig.ecosystem;
					const prevNetworkId = promotedNetworkIdRef.current;
					const nextNetworkId = newRuntime.networkConfig.id;
					setWalletSessionRegistry((prevRegistry) => upsertWalletSession(prevRegistry, {
						ecosystem,
						lastConfiguredNetworkId: nextNetworkId,
						providerComponent,
						hooks
					}));
					setGlobalActiveRuntime(newRuntime);
					setActiveWalletSessionEcosystem(ecosystem);
					promotedNetworkIdRef.current = nextNetworkId;
					if (prevNetworkId && prevNetworkId !== nextNetworkId) releaseRuntime(prevNetworkId);
				}
			} catch (error) {
				if (!abortController.signal.aborted) logger.error("[WSP loadRuntimeAndConfigureUi]", "Error during runtime UI setup:", error);
			}
			else if (!newRuntime && !newIsLoading) {
				if (!abortController.signal.aborted) {
					const prevNetworkId = promotedNetworkIdRef.current;
					setGlobalActiveRuntime(null);
					setActiveWalletSessionEcosystem(null);
					if (prevNetworkId) {
						releaseRuntime(prevNetworkId);
						promotedNetworkIdRef.current = null;
					}
				}
			}
		}
		loadRuntimeAndConfigureUi();
		return () => abortController.abort();
	}, [
		currentGlobalNetworkConfig,
		getRuntimeForNetwork,
		releaseRuntime,
		loadConfigModule,
		uiKitConfigVersion,
		programmaticUiKitConfig
	]);
	/**
	* Callback to set the globally active network ID.
	* Also clears dependent states if the network ID is cleared.
	*/
	const setActiveNetworkIdCallback = useCallback((networkId) => {
		logger.info("WalletStateProvider", `Setting global network ID to: ${networkId}`);
		setCurrentGlobalNetworkIdState(networkId);
		if (!networkId) {
			setCurrentGlobalNetworkConfig(null);
			setGlobalActiveRuntime(null);
			setIsGlobalRuntimeLoading(false);
			setActiveWalletSessionEcosystem(null);
		}
	}, []);
	/**
	* Callback to explicitly trigger a re-configuration of the active runtime's UI kit.
	* This is useful when a UI kit setting changes (e.g., via a wizard) without a network change.
	*/
	const reconfigureActiveUiKit = useCallback((uiKitConfig) => {
		logger.info("WalletStateProvider", "Explicitly triggering UI kit re-configuration by bumping version.", uiKitConfig);
		setProgrammaticUiKitConfig(uiKitConfig);
		setUiKitConfigVersion((v) => v + 1);
	}, [setProgrammaticUiKitConfig, setUiKitConfigVersion]);
	const activeWalletSession = useMemo(() => getWalletSession(walletSessionRegistry, activeWalletSessionEcosystem), [walletSessionRegistry, activeWalletSessionEcosystem]);
	const walletFacadeHooks = activeWalletSession?.hooks ?? null;
	const contextValue = useMemo(() => ({
		activeNetworkId: currentGlobalNetworkId,
		setActiveNetworkId: setActiveNetworkIdCallback,
		activeNetworkConfig: currentGlobalNetworkConfig,
		activeRuntime: globalActiveRuntime,
		isRuntimeLoading: isGlobalRuntimeLoading,
		walletFacadeHooks,
		reconfigureActiveUiKit
	}), [
		currentGlobalNetworkId,
		setActiveNetworkIdCallback,
		currentGlobalNetworkConfig,
		globalActiveRuntime,
		isGlobalRuntimeLoading,
		walletFacadeHooks,
		reconfigureActiveUiKit
	]);
	const ActualProviderToRender = activeWalletSession?.providerComponent ?? null;
	let childrenToRender;
	if (ActualProviderToRender) {
		const key = activeWalletSessionEcosystem || "unknown";
		logger.debug("[WSP RENDER]", "Rendering runtime-provided UI context provider:", ActualProviderToRender.displayName || ActualProviderToRender.name || "UnknownComponent", "with key:", key);
		childrenToRender = /* @__PURE__ */ jsx(ActualProviderToRender, { children }, key);
	} else {
		logger.debug("[WSP RENDER]", "No runtime UI context provider to render. Rendering direct children.");
		childrenToRender = children;
	}
	return /* @__PURE__ */ jsx(WalletStateContext.Provider, {
		value: contextValue,
		children: childrenToRender
	});
}

//#endregion
//#region src/hooks/AnalyticsContext.tsx
/**
* Analytics context for providing analytics functionality to React components.
* Must be used within an AnalyticsProvider.
*/
const AnalyticsContext = createContext(null);
/**
* Internal hook to access analytics context.
* Throws an error if used outside of an AnalyticsProvider.
*
* @internal
* @throws Error if used outside of AnalyticsProvider
*/
const useAnalyticsContext = () => {
	const context = useContext(AnalyticsContext);
	if (!context) throw new Error("useAnalyticsContext must be used within an AnalyticsProvider");
	return context;
};

//#endregion
//#region src/hooks/AnalyticsProvider.tsx
/**
* Analytics Provider component.
* Provides analytics functionality throughout the React component tree.
*
* @example
* ```tsx
* function App() {
*   return (
*     <AnalyticsProvider tagId={import.meta.env.VITE_GA_TAG_ID} autoInit={true}>
*       <YourApp />
*     </AnalyticsProvider>
*   );
* }
* ```
*/
const AnalyticsProvider = ({ tagId, autoInit = true, children }) => {
	useEffect(() => {
		if (autoInit && tagId) AnalyticsService.initialize(tagId);
	}, [tagId, autoInit]);
	const contextValue = useMemo(() => ({
		tagId,
		isEnabled: () => AnalyticsService.isEnabled(),
		initialize: (tagIdOverride) => {
			const effectiveTagId = tagIdOverride || tagId;
			if (effectiveTagId) AnalyticsService.initialize(effectiveTagId);
		},
		trackEvent: (eventName, parameters) => {
			try {
				AnalyticsService.trackEvent(eventName, parameters);
			} catch (error) {
				logger.error("AnalyticsProvider", "Error tracking event:", error);
			}
		},
		trackPageView: (pageName, pagePath) => {
			try {
				AnalyticsService.trackPageView(pageName, pagePath);
			} catch (error) {
				logger.error("AnalyticsProvider", "Error tracking page view:", error);
			}
		},
		trackNetworkSelection: (networkId, ecosystem) => {
			try {
				AnalyticsService.trackNetworkSelection(networkId, ecosystem);
			} catch (error) {
				logger.error("AnalyticsProvider", "Error tracking network selection:", error);
			}
		}
	}), [tagId]);
	return /* @__PURE__ */ jsx(AnalyticsContext.Provider, {
		value: contextValue,
		children
	});
};

//#endregion
//#region src/hooks/useAnalytics.ts
/**
* Custom hook for accessing analytics functionality.
*
* This hook provides a convenient interface for tracking user interactions
* throughout the application. It must be used within an AnalyticsProvider.
*
* For app-specific tracking methods, create a wrapper hook that uses this
* hook and adds your custom tracking functions.
*
* @example
* ```tsx
* // Basic usage
* function MyComponent() {
*   const { trackEvent, trackPageView, isEnabled } = useAnalytics();
*
*   const handleClick = () => {
*     trackEvent('button_clicked', { button_name: 'submit' });
*   };
*
*   return (
*     <div>
*       Analytics enabled: {isEnabled().toString()}
*       <button onClick={handleClick}>Submit</button>
*     </div>
*   );
* }
* ```
*
* @example
* ```tsx
* // Creating app-specific wrapper hook
* function useMyAppAnalytics() {
*   const analytics = useAnalytics();
*
*   return {
*     ...analytics,
*     trackFormSubmit: (formName: string) => {
*       analytics.trackEvent('form_submitted', { form_name: formName });
*     },
*   };
* }
* ```
*
* @returns Analytics context with tracking methods and state
* @throws Error if used outside of AnalyticsProvider
*/
const useAnalytics = () => {
	try {
		return useAnalyticsContext();
	} catch {
		throw new Error("useAnalytics must be used within an AnalyticsProvider");
	}
};

//#endregion
//#region src/hooks/useWalletComponents.ts
/**
* Hook that provides direct access to wallet UI components from the active runtime.
*
* Use this hook when you need full control over the layout and composition of
* wallet components. For standard layouts, prefer using `WalletConnectionUI`
* with its props forwarding capabilities.
*
* @returns The wallet components object, or null if no runtime is active or
*          the runtime doesn't provide wallet components.
*
* @example
* ```tsx
* import { useWalletComponents } from '@openzeppelin/ui-react';
*
* function CustomWalletSection() {
*   const walletComponents = useWalletComponents();
*
*   if (!walletComponents) {
*     return <p>Loading wallet...</p>;
*   }
*
*   const { ConnectButton, NetworkSwitcher, AccountDisplay } = walletComponents;
*
*   return (
*     <div className="flex flex-col gap-4">
*       {ConnectButton && (
*         <ConnectButton
*           size="xl"
*           variant="outline"
*           fullWidth
*           className="font-semibold"
*         />
*       )}
*       <div className="flex gap-2">
*         {NetworkSwitcher && <NetworkSwitcher size="sm" />}
*         {AccountDisplay && <AccountDisplay size="sm" />}
*       </div>
*     </div>
*   );
* }
* ```
*/
function useWalletComponents() {
	const { activeNetworkConfig, activeRuntime, isRuntimeLoading } = useWalletState();
	const activeUiKit = activeRuntime?.uiKit;
	if (!!(isRuntimeLoading && activeRuntime?.networkConfig?.ecosystem && activeNetworkConfig?.ecosystem && activeRuntime.networkConfig.ecosystem !== activeNetworkConfig.ecosystem) || !activeUiKit || typeof activeUiKit.getEcosystemWalletComponents !== "function") return null;
	try {
		return activeUiKit.getEcosystemWalletComponents() ?? null;
	} catch {
		return null;
	}
}

//#endregion
//#region src/hooks/useDerivedAccountStatus.ts
const defaultAccountStatus = {
	isConnected: false,
	isConnecting: false,
	isDisconnected: true,
	isReconnecting: false,
	status: "disconnected",
	address: void 0,
	chainId: void 0
};
/**
* A custom hook that consumes useWalletState to get the walletFacadeHooks,
* then calls the useAccount facade hook (if available) and returns a structured,
* safely-accessed account status (isConnected, address, chainId).
* Provides default values if the hook or its properties are unavailable.
*/
function useDerivedAccountStatus() {
	const { walletFacadeHooks } = useWalletState();
	const accountHookOutput = walletFacadeHooks?.useAccount ? walletFacadeHooks.useAccount() : void 0;
	if (isRecordWithProperties(accountHookOutput)) return {
		isConnected: "isConnected" in accountHookOutput && typeof accountHookOutput.isConnected === "boolean" ? accountHookOutput.isConnected : defaultAccountStatus.isConnected,
		isConnecting: "isConnecting" in accountHookOutput && typeof accountHookOutput.isConnecting === "boolean" ? accountHookOutput.isConnecting : defaultAccountStatus.isConnecting,
		isDisconnected: "isDisconnected" in accountHookOutput && typeof accountHookOutput.isDisconnected === "boolean" ? accountHookOutput.isDisconnected : defaultAccountStatus.isDisconnected,
		isReconnecting: "isReconnecting" in accountHookOutput && typeof accountHookOutput.isReconnecting === "boolean" ? accountHookOutput.isReconnecting : defaultAccountStatus.isReconnecting,
		status: "status" in accountHookOutput && typeof accountHookOutput.status === "string" ? accountHookOutput.status : defaultAccountStatus.status,
		address: "address" in accountHookOutput && typeof accountHookOutput.address === "string" ? accountHookOutput.address : defaultAccountStatus.address,
		chainId: "chainId" in accountHookOutput && typeof accountHookOutput.chainId === "number" ? accountHookOutput.chainId : defaultAccountStatus.chainId
	};
	return defaultAccountStatus;
}

//#endregion
//#region src/hooks/useDerivedSwitchChainStatus.ts
const defaultSwitchChainStatus = {
	switchChain: void 0,
	isSwitching: false,
	error: null
};
/**
* A custom hook that consumes `useWalletState` to get `walletFacadeHooks`,
* then calls the `useSwitchChain` facade hook (if available) and returns a structured,
* safely-accessed status and control function for network switching.
* Provides default values if the hook or its properties are unavailable.
*/
function useDerivedSwitchChainStatus() {
	const { walletFacadeHooks } = useWalletState();
	const switchChainHookOutput = walletFacadeHooks?.useSwitchChain ? walletFacadeHooks.useSwitchChain() : void 0;
	if (isRecordWithProperties(switchChainHookOutput)) return {
		switchChain: "switchChain" in switchChainHookOutput && typeof switchChainHookOutput.switchChain === "function" ? switchChainHookOutput.switchChain : defaultSwitchChainStatus.switchChain,
		isSwitching: "isPending" in switchChainHookOutput && typeof switchChainHookOutput.isPending === "boolean" ? switchChainHookOutput.isPending : defaultSwitchChainStatus.isSwitching,
		error: "error" in switchChainHookOutput && switchChainHookOutput.error instanceof Error ? switchChainHookOutput.error : defaultSwitchChainStatus.error
	};
	return defaultSwitchChainStatus;
}

//#endregion
//#region src/hooks/useDerivedChainInfo.ts
const defaultChainInfo = {
	currentChainId: void 0,
	availableChains: []
};
/**
* A custom hook that consumes `useWalletState` to get `walletFacadeHooks`,
* then calls the `useChainId` and `useChains` facade hooks (if available)
* and returns a structured object with this information.
* Provides default values if the hooks or their properties are unavailable.
*/
function useDerivedChainInfo() {
	const { walletFacadeHooks } = useWalletState();
	let chainIdToReturn = defaultChainInfo.currentChainId;
	const chainIdHookOutput = walletFacadeHooks?.useChainId ? walletFacadeHooks.useChainId() : void 0;
	if (typeof chainIdHookOutput === "number") chainIdToReturn = chainIdHookOutput;
	else if (chainIdHookOutput !== void 0) logger.warn("useDerivedChainInfo", "useChainId facade hook returned non-numeric value:", chainIdHookOutput);
	let chainsToReturn = defaultChainInfo.availableChains;
	const chainsHookOutput = walletFacadeHooks?.useChains ? walletFacadeHooks.useChains() : void 0;
	if (Array.isArray(chainsHookOutput)) chainsToReturn = chainsHookOutput;
	else if (chainsHookOutput !== void 0) logger.warn("useDerivedChainInfo", "useChains facade hook returned non-array value:", chainsHookOutput);
	return {
		currentChainId: chainIdToReturn,
		availableChains: chainsToReturn
	};
}

//#endregion
//#region src/hooks/useDerivedConnectStatus.ts
const defaultConnectStatus = {
	connect: void 0,
	connectors: [],
	isConnecting: false,
	error: null,
	pendingConnector: void 0
};
/**
* A custom hook that consumes `useWalletState` to get `walletFacadeHooks`,
* then calls the `useConnect` facade hook (if available) and returns a structured,
* safely-accessed status and control functions for wallet connection.
*/
function useDerivedConnectStatus() {
	const { walletFacadeHooks } = useWalletState();
	const connectHookOutput = walletFacadeHooks?.useConnect ? walletFacadeHooks.useConnect() : void 0;
	if (isRecordWithProperties(connectHookOutput)) return {
		connect: "connect" in connectHookOutput && typeof connectHookOutput.connect === "function" ? connectHookOutput.connect : defaultConnectStatus.connect,
		connectors: "connectors" in connectHookOutput && Array.isArray(connectHookOutput.connectors) ? connectHookOutput.connectors : defaultConnectStatus.connectors,
		isConnecting: "isPending" in connectHookOutput && typeof connectHookOutput.isPending === "boolean" ? connectHookOutput.isPending : "isLoading" in connectHookOutput && typeof connectHookOutput.isLoading === "boolean" ? connectHookOutput.isLoading : defaultConnectStatus.isConnecting,
		error: "error" in connectHookOutput && connectHookOutput.error instanceof Error ? connectHookOutput.error : defaultConnectStatus.error,
		pendingConnector: "pendingConnector" in connectHookOutput && typeof connectHookOutput.pendingConnector === "object" ? connectHookOutput.pendingConnector : defaultConnectStatus.pendingConnector
	};
	return defaultConnectStatus;
}

//#endregion
//#region src/hooks/useDerivedDisconnect.ts
const defaultDisconnectStatus = {
	disconnect: void 0,
	isDisconnecting: false,
	error: null
};
/**
* A custom hook that consumes `useWalletState` to get `walletFacadeHooks`,
* then calls the `useDisconnect` facade hook (if available) and returns a structured,
* safely-accessed status and control function for wallet disconnection.
*/
function useDerivedDisconnect() {
	const { walletFacadeHooks } = useWalletState();
	const disconnectHookOutput = walletFacadeHooks?.useDisconnect ? walletFacadeHooks.useDisconnect() : void 0;
	if (isRecordWithProperties(disconnectHookOutput)) return {
		disconnect: "disconnect" in disconnectHookOutput && typeof disconnectHookOutput.disconnect === "function" ? disconnectHookOutput.disconnect : defaultDisconnectStatus.disconnect,
		isDisconnecting: "isPending" in disconnectHookOutput && typeof disconnectHookOutput.isPending === "boolean" ? disconnectHookOutput.isPending : "isLoading" in disconnectHookOutput && typeof disconnectHookOutput.isLoading === "boolean" ? disconnectHookOutput.isLoading : defaultDisconnectStatus.isDisconnecting,
		error: "error" in disconnectHookOutput && disconnectHookOutput.error instanceof Error ? disconnectHookOutput.error : defaultDisconnectStatus.error
	};
	return defaultDisconnectStatus;
}

//#endregion
//#region src/hooks/useWalletReconnectionHandler.ts
/**
* Hook that detects wallet reconnection and re-queues network switch if needed.
*
* When a user disconnects their wallet and then reconnects in the same session,
* the wallet may connect to a different chain than what's selected in the app.
* This hook detects that scenario and invokes a callback to re-queue the network switch.
*
* @param selectedNetworkConfigId - Currently selected network in the app
* @param selectedCapability - Currently active wallet capability instance
* @param networkToSwitchTo - Current network switch queue state (null if no switch pending)
* @param onRequeueSwitch - Callback invoked when a network switch should be re-queued
*/
function useWalletReconnectionHandler(selectedNetworkConfigId, selectedCapability, networkToSwitchTo, onRequeueSwitch) {
	const { isConnected, chainId: walletChainId } = useDerivedAccountStatus();
	const prevConnectedRef = useRef(isConnected);
	useEffect(() => {
		const isReconnection = !prevConnectedRef.current && isConnected;
		prevConnectedRef.current = isConnected;
		if (!isReconnection || !selectedNetworkConfigId || !selectedCapability) return;
		if (networkToSwitchTo === selectedNetworkConfigId) return;
		const selectedNetworkConfig = selectedCapability.networkConfig;
		if (!("chainId" in selectedNetworkConfig) || !walletChainId) return;
		const targetChainId = Number(selectedNetworkConfig.chainId);
		if (walletChainId !== targetChainId) {
			logger.info("useWalletReconnectionHandler", `Wallet reconnected with chain ${walletChainId}, but selected network requires ${targetChainId}. Re-queueing switch.`);
			onRequeueSwitch(selectedNetworkConfigId);
		}
	}, [
		isConnected,
		walletChainId,
		selectedNetworkConfigId,
		selectedCapability,
		networkToSwitchTo,
		onRequeueSwitch
	]);
}

//#endregion
//#region src/hooks/nameResolution/resolutionConfig.ts
/**
* Default resolution config. Values sit within the spec's "seconds-to-minutes"
* caching guidance; forward debounce is 300ms (typed char-by-char), reverse is 0.
*/
const DEFAULT_CONFIG = {
	staleTimeMs: 6e4,
	gcTimeMs: 3e5,
	forwardDebounceMs: 300,
	reverseDebounceMs: 0,
	transientRetryCount: 2
};
/** Stable prefix for every resolution cache key — namespaces this package's keys. */
const RESOLUTION_KEY_PREFIX = "oz-name-resolution";
const runtimeInstanceIds = /* @__PURE__ */ new WeakMap();
let nextRuntimeInstanceId = 1;
/**
* Assign a stable, serializable id to each {@link EcosystemRuntime} object identity.
* A disposed-and-recreated runtime (e.g. SF-4 opt-in toggle after INV-218 flush) gets
* a new id so resolution cache keys miss and results refresh (INV-230).
*/
function getRuntimeInstanceId(runtime) {
	if (!runtime) return 0;
	let id = runtimeInstanceIds.get(runtime);
	if (id === void 0) {
		id = nextRuntimeInstanceId;
		nextRuntimeInstanceId += 1;
		runtimeInstanceIds.set(runtime, id);
	}
	return id;
}
/**
* Build a network-scoped, namespace-separated cache key (INV-40). A name resolves
* differently per network and provenance can be chain-scoped, so `networkId` is
* part of the key; `namespace` keeps forward and reverse lookups disjoint;
* `runtimeInstanceId` scopes entries to the active capability instance (INV-230).
*
* @param namespace           - `'name'` (forward) or `'addr'` (reverse).
* @param networkId           - Active network id (`''` when no network is selected).
* @param normalizedInput     - Trimmed + lowercased input (INV-26).
* @param runtimeInstanceId   - Active runtime instance discriminator (`0` when absent).
* @returns A readonly tuple suitable for a TanStack Query `queryKey`.
*/
function buildResolutionKey(namespace, networkId, normalizedInput, runtimeInstanceId = 0) {
	return [
		RESOLUTION_KEY_PREFIX,
		namespace,
		networkId,
		normalizedInput,
		runtimeInstanceId
	];
}
/**
* Whether a resolution error is transient (worth retrying) versus a definitive,
* stable answer (INV-47). Exhaustive over SF-1's closed error union with no
* `default`: adding a new error code to `@openzeppelin/ui-types` will fail to
* compile here until it is classified — deliberately coupling SF-2 to SF-1 INV-7.
*
* @param error - A typed {@link NameResolutionError}.
* @returns `true` for transient failures (timeout / gateway / adapter), `false`
*   for definitive negatives and unsupported-network answers.
*/
function isTransientError(error) {
	switch (error.code) {
		case "RESOLUTION_TIMEOUT":
		case "EXTERNAL_GATEWAY_ERROR":
		case "ADAPTER_ERROR": return true;
		case "NAME_NOT_FOUND":
		case "ADDRESS_NOT_FOUND":
		case "UNSUPPORTED_NAME":
		case "UNSUPPORTED_NETWORK": return false;
	}
}
/**
* Construct a QueryClient tuned for name resolution: ambient refetch triggers are
* disabled at the client level (INV-41) so a resolved value never silently
* changes under the user on window focus or network reconnect.
*/
function createResolutionQueryClient() {
	return new QueryClient({ defaultOptions: { queries: {
		refetchOnWindowFocus: false,
		refetchOnReconnect: false
	} } });
}
/**
* Process-global key for the default resolution QueryClient. Uses `Symbol.for`
* so every duplicated module instance (Vite pre-bundling, npm-installed adapters)
* shares ONE client — the same cross-bundle-singleton pattern as
* `WalletStateContext`. This is what makes the cache shared with zero wiring
* (SC-001) and enables cross-instance dedup / warm-cache reuse (INV-33 / INV-37 / INV-48).
*/
const DEFAULT_CLIENT_KEY = Symbol.for("@openzeppelin/ui-react/NameResolutionQueryClient");
/**
* Lazily create (exactly once) and return the process-global resolution
* QueryClient used when no `NameResolutionProvider` is mounted (INV-48).
*
* @returns The shared default resolution QueryClient (stable across calls).
*/
function getDefaultResolutionQueryClient() {
	const globalScope = globalThis;
	if (!globalScope[DEFAULT_CLIENT_KEY]) globalScope[DEFAULT_CLIENT_KEY] = createResolutionQueryClient();
	return globalScope[DEFAULT_CLIENT_KEY];
}

//#endregion
//#region src/hooks/nameResolution/NameResolutionContext.ts
/**
* Process-global key for the context object. Uses `Symbol.for` so all duplicated
* module instances share ONE React context — the same cross-bundle-singleton
* pattern as `WalletStateContext` (bundler pre-bundling / npm-installed adapters).
*/
const CONTEXT_KEY = Symbol.for("@openzeppelin/ui-react/NameResolutionContext");
function getOrCreateSharedContext() {
	const globalScope = globalThis;
	if (!globalScope[CONTEXT_KEY]) globalScope[CONTEXT_KEY] = createContext(null);
	return globalScope[CONTEXT_KEY];
}
/**
* React context for resolution. `null` default triggers the module-singleton
* fallback in {@link useNameResolutionContext} — no Provider is required (INV-48).
*/
const NameResolutionContext = getOrCreateSharedContext();
/**
* Stable fallback value used when no Provider is mounted. Cached per module so the
* returned reference is referentially stable across renders (INV-38); it wraps the
* process-global default client, so caches are still shared even across bundles.
*/
let fallbackValue;
function getFallbackContextValue() {
	if (!fallbackValue) fallbackValue = {
		queryClient: getDefaultResolutionQueryClient(),
		config: DEFAULT_CONFIG
	};
	return fallbackValue;
}
/**
* Read the resolution context, falling back to the zero-wiring default (global
* singleton client + {@link DEFAULT_CONFIG}) when no `NameResolutionProvider` is
* mounted (INV-48). Never throws for a missing provider.
*
* @returns The active {@link NameResolutionContextValue}.
*/
function useNameResolutionContext() {
	return useContext(NameResolutionContext) ?? getFallbackContextValue();
}

//#endregion
//#region src/hooks/nameResolution/resolutionState.ts
/**
* Internal error used to bridge SF-1's `ok: false` results (and defensively-caught
* adapter throws) into TanStack Query's thrown-error channel, carrying the typed
* {@link NameResolutionError} through so the mapping step can surface it unchanged
* (INV-43). Never leaks past the hook boundary.
*/
var ResolutionQueryError = class extends Error {
	resolutionError;
	/** @param resolutionError - The typed error to carry through react-query. */
	constructor(resolutionError) {
		super(`name resolution failed: ${resolutionError.code}`);
		this.name = "ResolutionQueryError";
		this.resolutionError = resolutionError;
	}
};
/**
* Convert any value from react-query's error channel into a typed, closed-union
* {@link NameResolutionError} (INV-43). A {@link ResolutionQueryError} is
* unwrapped verbatim; anything else (a react-query internal error) is mapped to
* `ADAPTER_ERROR` so no untyped throw reaches a component.
*
* @param error - The raw `query.error` value (`unknown`).
* @returns A typed error drawn only from SF-1's closed union.
*/
function toNameResolutionError(error) {
	if (error instanceof ResolutionQueryError) return error.resolutionError;
	return {
		code: "ADAPTER_ERROR",
		message: error instanceof Error ? error.message : String(error),
		cause: error
	};
}
/**
* Map a settled query's state to exactly one non-gate {@link EngineResult} arm
* (INV-42, INV-44). The `input` echoed here is the debounced value the query was
* keyed on, so `data`/`error` are always paired with the input that produced them
* (INV-24). Success without data degrades to `loading` rather than a resolved arm
* missing its payload.
*
* @param input - The debounced, normalized input keying this query.
* @param query - The settled query state.
* @param retry - Bound `refetch` for the `error` arm (INV-34).
* @returns The `resolved`, `error`, or `loading` arm.
*/
function mapSettledQuery(input, query, retry) {
	if (query.isSuccess && query.data !== void 0) return {
		status: "resolved",
		input,
		data: query.data
	};
	if (query.isError) return {
		status: "error",
		input,
		error: toNameResolutionError(query.error),
		retry
	};
	return {
		status: "loading",
		input
	};
}

//#endregion
//#region src/hooks/nameResolution/useResolutionEngine.ts
const LOG_SCOPE = "useResolutionEngine";
/** No-op retry for synthesized errors that never entered the query path (INV-45). */
const NOOP_RETRY = () => {};
/**
* Shared engine behind `useResolveName` / `useResolveAddress`. Owns input
* normalization, debouncing, the `useQuery` wiring (explicit-client form), the
* capability-absence gate, and the mapping to a direction-agnostic
* {@link EngineResult}. Not exported from the package.
*/
function useResolutionEngine(params) {
	const { input, namespace, debounceMs, enabled, shouldAttempt, getMethod, runtimeSource } = params;
	const walletState = useContext(WalletStateContext);
	const activeRuntime = runtimeSource ? runtimeSource.runtime : walletState?.activeRuntime ?? null;
	const activeNetworkId = runtimeSource ? runtimeSource.networkId : walletState?.activeNetworkId ?? null;
	const isRuntimeLoading = runtimeSource ? runtimeSource.isRuntimeLoading : walletState?.isRuntimeLoading ?? false;
	const { queryClient, config } = useNameResolutionContext();
	const trimmedLive = (input ?? "").trim();
	const normalizedLive = trimmedLive.toLowerCase();
	const [debouncedTrimmed, setDebouncedTrimmed] = useState(trimmedLive);
	const seededRef = useRef(false);
	useEffect(() => {
		if (!seededRef.current) {
			seededRef.current = true;
			return;
		}
		if (debounceMs <= 0) {
			setDebouncedTrimmed(trimmedLive);
			return;
		}
		const timer = setTimeout(() => setDebouncedTrimmed(trimmedLive), debounceMs);
		return () => clearTimeout(timer);
	}, [trimmedLive, debounceMs]);
	const effectiveTrimmed = debounceMs <= 0 ? trimmedLive : debouncedTrimmed;
	const normalizedKey = effectiveTrimmed.toLowerCase();
	const capability = activeRuntime?.nameResolution;
	const networkId = activeNetworkId ?? "";
	const runtimeInstanceId = getRuntimeInstanceId(activeRuntime);
	const method = capability ? getMethod(capability) : void 0;
	const liveAttemptable = capability ? shouldAttempt(capability, normalizedLive) : false;
	const keyAttemptable = capability ? shouldAttempt(capability, normalizedKey) : false;
	const queryEnabled = enabled && trimmedLive !== "" && capability != null && method != null && normalizedKey !== "" && keyAttemptable;
	const query = useQuery({
		queryKey: buildResolutionKey(namespace, networkId, normalizedKey, runtimeInstanceId),
		queryFn: async () => {
			if (!method) throw new ResolutionQueryError({
				code: "ADAPTER_ERROR",
				message: "resolution method unexpectedly missing"
			});
			const adapterArg = namespace === "addr" ? effectiveTrimmed : normalizedKey;
			let result;
			try {
				result = await method(adapterArg);
			} catch (cause) {
				logger.warn(LOG_SCOPE, "resolution method threw instead of returning ok:false", cause);
				throw new ResolutionQueryError({
					code: "ADAPTER_ERROR",
					message: cause instanceof Error ? cause.message : String(cause),
					cause
				});
			}
			if (!result.ok) throw new ResolutionQueryError(result.error);
			return result.value;
		},
		enabled: queryEnabled,
		staleTime: config.staleTimeMs,
		gcTime: config.gcTimeMs,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		retry: (failureCount, error) => (error instanceof ResolutionQueryError ? isTransientError(error.resolutionError) : true) && failureCount < config.transientRetryCount
	}, queryClient);
	if (!enabled) return { status: "idle" };
	if (trimmedLive === "") return { status: "idle" };
	if (capability) {
		if (!liveAttemptable) return { status: "idle" };
		if (!method) return unsupportedNetwork(normalizedLive, networkId);
		if (normalizedKey !== normalizedLive) return {
			status: "debouncing",
			input: normalizedLive
		};
	} else {
		if (isRuntimeLoading) return {
			status: "loading",
			input: normalizedLive
		};
		return unsupportedNetwork(normalizedLive, networkId);
	}
	const retry = () => {
		query.refetch();
	};
	return mapSettledQuery(normalizedKey, query, retry);
}
/**
* Synthesize an `UNSUPPORTED_NETWORK` error arm without touching react-query
* (INV-45). Drawn from SF-1's closed union so it is indistinguishable from an
* adapter-returned `UNSUPPORTED_NETWORK`; `networkId` may be `''` (INV-49). Retry
* is a no-op — there is no query to refetch.
*/
function unsupportedNetwork(input, networkId) {
	return {
		status: "error",
		input,
		error: {
			code: "UNSUPPORTED_NETWORK",
			networkId
		},
		retry: NOOP_RETRY
	};
}

//#endregion
//#region src/hooks/nameResolution/useResolveName.ts
/**
* Forward-resolve a name to an address. Soft-reads the active runtime from
* `WalletStateContext` (never throws when no provider is mounted — degrades to
* idle / UNSUPPORTED_NETWORK) and calls `runtime.nameResolution?.resolveName`.
* Debounces `name`, caches per (network, name) via the owned QueryClient, and is
* protected against out-of-order responses (distinct inputs → distinct query keys).
*
* Returns a discriminated union keyed on `status`; it never throws for expected
* failures (they surface in the `error` arm) and never pairs a name with a
* different name's resolved address (INV-24).
*
* @param name    - The name to resolve. `null` / empty / a value failing the
*   adapter's `isValidName` yields `status: 'idle'` (never `error`).
* @param options - `debounceMs` / `enabled` overrides.
* @returns The current {@link UseResolveNameResult}.
*/
function useResolveName(name, options) {
	const { config } = useNameResolutionContext();
	return toNameResult(useResolutionEngine({
		input: name,
		namespace: "name",
		debounceMs: options?.debounceMs ?? config.forwardDebounceMs,
		enabled: options?.enabled ?? true,
		shouldAttempt: (cap, normalized) => cap.isValidName(normalized),
		getMethod: (cap) => cap.resolveName
	}));
}
/**
* Remap the engine's generic `input` to `name` (INV-24: keyed off the debounced
* input carried in the engine result, not the live prop). Exhaustive over the
* union so a new status cannot silently fall through (INV-23 / INV-42).
*/
function toNameResult(engine) {
	switch (engine.status) {
		case "idle": return { status: "idle" };
		case "debouncing": return {
			status: "debouncing",
			name: engine.input
		};
		case "loading": return {
			status: "loading",
			name: engine.input
		};
		case "resolved": return {
			status: "resolved",
			name: engine.input,
			data: engine.data
		};
		case "error": return {
			status: "error",
			name: engine.input,
			error: engine.error,
			retry: engine.retry
		};
	}
}

//#endregion
//#region src/hooks/nameResolution/mapAddressEngineResult.ts
/**
* Remap the engine's generic `input` to `address` (INV-24). A `debouncing` arm —
* only reachable when a caller passes a non-zero reverse `debounceMs` — collapses
* to `loading` so {@link UseResolveAddressResult} keeps no `debouncing` variant.
*/
function mapAddressEngineResult(engine) {
	switch (engine.status) {
		case "idle": return { status: "idle" };
		case "debouncing":
		case "loading": return {
			status: "loading",
			address: engine.input
		};
		case "resolved": return {
			status: "resolved",
			address: engine.input,
			data: engine.data
		};
		case "error": return {
			status: "error",
			address: engine.input,
			error: engine.error,
			retry: engine.retry
		};
	}
}

//#endregion
//#region src/hooks/nameResolution/useNetworkRuntimeSource.ts
/**
* Soft-reads {@link RuntimeContext} (never throws) and requests the registry
* runtime for `network`. Call during render so loading state updates propagate.
*
* Does not mutate {@link WalletStateProvider}'s active network — wallet-global
* runtime is never consulted when a scoped network is supplied (INV-154).
*/
function useNetworkRuntimeSource(network) {
	const runtimeContext = useContext(RuntimeContext);
	if (!network) return {
		runtime: null,
		networkId: "",
		isRuntimeLoading: false
	};
	if (!runtimeContext) return {
		runtime: null,
		networkId: network.id,
		isRuntimeLoading: false
	};
	const { runtime, isLoading } = runtimeContext.getRuntimeForNetwork(network);
	return {
		runtime,
		networkId: network.id,
		isRuntimeLoading: isLoading
	};
}

//#endregion
//#region src/hooks/nameResolution/useResolveAddress.ts
/**
* Reverse-resolve an address to a name. Soft-reads the active runtime from
* `WalletStateContext` (never throws when no provider is mounted — degrades to
* idle / UNSUPPORTED_NETWORK) and calls `runtime.nameResolution?.resolveAddress`.
* Pass `options.network` to scope resolution to a specific network without
* changing the wallet's active network. Caches per (network, address) via the
* owned QueryClient. Not debounced by default — addresses are pasted, not
* typed char-by-char.
*
* Applies no client-side address-shape check (INV-32): resolution is attempted on
* any non-empty input and malformed addresses are rejected via the adapter's typed
* error union. Address-shape validation is SF-3's concern.
*
* @param address - The address to reverse-resolve. `null` / empty yields
*   `status: 'idle'`.
* @param options - `debounceMs` / `enabled` / `network` overrides.
* @returns The current {@link UseResolveAddressResult}.
*/
function useResolveAddress(address, options) {
	const { config } = useNameResolutionContext();
	const debounceMs = options?.debounceMs ?? config.reverseDebounceMs;
	const enabled = options?.enabled ?? true;
	const networkSource = useNetworkRuntimeSource(options?.network ?? null);
	return mapAddressEngineResult(useResolutionEngine({
		input: address,
		namespace: "addr",
		debounceMs,
		enabled,
		shouldAttempt: () => true,
		getMethod: (cap) => cap.resolveAddress,
		runtimeSource: options?.network != null ? networkSource : void 0
	}));
}

//#endregion
//#region src/hooks/nameResolution/NameResolutionProvider.tsx
/**
* Optional provider that overrides resolution config and/or the QueryClient for
* its subtree. It does NOT render a `QueryClientProvider`: the client is passed
* explicitly to `useQuery` (INV-48), so no ambient react-query provider is needed.
*
* Config is merged field-by-field over {@link DEFAULT_CONFIG} (INV-30), and the
* context value is memoized so a Provider re-render does not cascade to every hook
* (INV-38).
*/
function NameResolutionProvider({ children, config, queryClient }) {
	const value = useMemo(() => ({
		queryClient: queryClient ?? getDefaultResolutionQueryClient(),
		config: {
			...DEFAULT_CONFIG,
			...config
		}
	}), [queryClient, config]);
	return /* @__PURE__ */ jsx(NameResolutionContext.Provider, {
		value,
		children
	});
}

//#endregion
//#region src/hooks/nameResolution/createNameResolver.ts
const EMPTY_RESOLVER$1 = {};
/**
* Projects a {@link NameResolutionCapability} into the SF-3 {@link NameResolver}
* seam with SF-2 cache-key parity (INV-119).
*/
function createNameResolverFromCapability(capability, networkId, runtimeInstanceId, queryClient, config) {
	if (!capability) return EMPTY_RESOLVER$1;
	const resolveNameMethod = capability.resolveName;
	return {
		isValidName: (name) => capability.isValidName(name),
		resolveName: resolveNameMethod ? async (name) => {
			const normalized = name.trim().toLowerCase();
			try {
				return {
					ok: true,
					value: await queryClient.fetchQuery({
						queryKey: buildResolutionKey("name", networkId, normalized, runtimeInstanceId),
						queryFn: async () => {
							let result;
							try {
								result = await resolveNameMethod(normalized);
							} catch (cause) {
								throw new ResolutionQueryError({
									code: "ADAPTER_ERROR",
									message: cause instanceof Error ? cause.message : String(cause),
									cause
								});
							}
							if (!result.ok) throw new ResolutionQueryError(result.error);
							return result.value;
						},
						staleTime: config.staleTimeMs,
						gcTime: config.gcTimeMs,
						retry: (failureCount, error) => (error instanceof ResolutionQueryError ? isTransientError(error.resolutionError) : true) && failureCount < config.transientRetryCount
					})
				};
			} catch (error) {
				return {
					ok: false,
					error: toNameResolutionError(error)
				};
			}
		} : void 0
	};
}

//#endregion
//#region src/hooks/nameResolution/useRuntimeNameResolver.ts
/**
* Stable empty resolver returned when no runtime / capability is available.
* No methods ⇒ the consuming field treats a typed name as unsupported and
* surfaces `UNSUPPORTED_NETWORK` (INV-119) while hex behavior stays legacy.
*/
const EMPTY_RESOLVER = {};
/**
* Project a runtime's `NameResolutionCapability` into the injected
* {@link NameResolver} seam consumed by `@openzeppelin/ui-components` (SF-3, D3).
* Mounted ambiently by the renderer:
*
* ```tsx
* <NameResolverProvider {...useRuntimeNameResolver()}>{form}</NameResolverProvider>
* ```
*
* Pass `scopedNetwork` to resolve against a specific network via
* {@link RuntimeProvider}'s registry without changing {@link WalletStateProvider}'s
* active network (e.g. address-book Add Alias dropdown).
*
* Each imperative call is backed by SF-2's **owned** resolution `QueryClient`
* via `fetchQuery`, reusing SF-2's exact query-key convention
* (`buildResolutionKey('name', networkId, normalizedName)`) — so a name
* resolved through the field and the same name resolved by a bare
* `useResolveName` hit the SAME cache entry: shared cache, dedupe, and
* out-of-order safety, never a parallel cache (INV-119).
*
* Degradation, in order (all method-omission, never a throw):
* - No `WalletStateProvider` mounted (wallet path) or no `RuntimeProvider`
*   (network path) → empty resolver. The context is read directly (not via
*   `useWalletState()`, which throws) so the ambient renderer mount stays safe
*   for wallet-less usage.
* - No active runtime, or runtime without the capability → empty resolver.
* - Capability without `resolveName` → `isValidName` only; forward unsupported.
*
* When the capability is present but the network itself is unsupported, the
* adapter's `resolveName` resolves `{ ok: false, error: UNSUPPORTED_NETWORK }`
* and that result passes through unchanged.
*
* @returns A referentially stable {@link NameResolver} for the active runtime.
*/
function useRuntimeNameResolver(scopedNetwork) {
	const walletState = useContext(WalletStateContext);
	const { queryClient, config } = useNameResolutionContext();
	const networkSource = useNetworkRuntimeSource(scopedNetwork ?? null);
	const useNetworkScoped = scopedNetwork != null;
	const activeRuntime = useNetworkScoped ? networkSource.runtime : walletState?.activeRuntime ?? null;
	const capability = activeRuntime?.nameResolution;
	const networkId = useNetworkScoped ? networkSource.networkId : walletState?.activeNetworkId ?? "";
	const runtimeInstanceId = getRuntimeInstanceId(activeRuntime);
	return useMemo(() => {
		if (!capability) return EMPTY_RESOLVER;
		return createNameResolverFromCapability(capability, networkId, runtimeInstanceId, queryClient, config);
	}, [
		capability,
		networkId,
		runtimeInstanceId,
		queryClient,
		config,
		useNetworkScoped ? networkSource.isRuntimeLoading : walletState?.isRuntimeLoading ?? false
	]);
}

//#endregion
//#region src/hooks/nameResolution/useNetworkNameResolver.ts
/**
* @deprecated Pass the network to {@link useRuntimeNameResolver} instead.
*/
function useNetworkNameResolver(network) {
	return useRuntimeNameResolver(network ?? void 0);
}

//#endregion
//#region src/hooks/nameResolution/useNetworkResolveAddress.ts
/**
* @deprecated Pass `network` in {@link UseResolveAddressOptions} to {@link useResolveAddress}.
*/
function useNetworkResolveAddress(address, network, options) {
	return useResolveAddress(address, network != null ? {
		...options,
		network
	} : options);
}

//#endregion
//#region src/hooks/runtime/runtimeCreationConfig.ts
/** Safe default: miss-fallback OFF; no uiKit override. INV-204 */
const DEFAULT_RUNTIME_CREATION_CONFIG = {};
/**
* Normalizes the opt-in flag to the adapter's strict-enable contract.
* UIKit uses this at the threading boundary so `false` and `undefined` never
* emit `enableMainnetL1MissFallback: false` into adapter options (absent = OFF).
*/
function isMainnetL1MissFallbackEnabled(options) {
	return options?.enableMainnetL1MissFallback === true;
}
/**
* Builds the `createRuntime` options bag for adapter threading.
* When OFF, `nameResolution` is omitted (INV-207). When ON, spreads only
* `{ enableMainnetL1MissFallback: true }` (INV-208).
*/
function buildCreateRuntimeOptions(options) {
	return {
		...options?.uiKit !== void 0 ? { uiKit: options.uiKit } : {},
		...isMainnetL1MissFallbackEnabled(options?.nameResolution) ? { nameResolution: { enableMainnetL1MissFallback: true } } : {}
	};
}

//#endregion
//#region src/hooks/runtime/createResolveRuntime.ts
/**
* Build a `resolveRuntime` callback for {@link RuntimeProvider} that forwards
* `CreateRuntimeOptions` to `ecosystemDefinition.createRuntime`.
*
* Does not cache — {@link RuntimeProvider} owns the per-network singleton registry.
*/
function createResolveRuntime(ecosystemDefinition, params) {
	const { profile, options } = params;
	const mergedOptions = buildCreateRuntimeOptions(options);
	return (networkConfig) => Promise.resolve(ecosystemDefinition.createRuntime(profile, networkConfig, mergedOptions));
}

//#endregion
//#region src/hooks/runtime/useResolveRuntime.ts
/**
* Memoized {@link createResolveRuntime} for React apps. Recompute when
* `ecosystemDefinition`, `profile`, or opt-in posture change (INV-217, INV-222).
*
* Changing the returned function identity MUST trigger runtime registry flush
* in {@link RuntimeProvider} (INV-218).
*/
function useResolveRuntime(ecosystemDefinition, params) {
	const { profile, options } = params;
	const uiKit = options?.uiKit;
	const enableMainnetL1MissFallback = options?.nameResolution?.enableMainnetL1MissFallback;
	return useMemo(() => createResolveRuntime(ecosystemDefinition, {
		profile,
		options: {
			...uiKit !== void 0 ? { uiKit } : {},
			...enableMainnetL1MissFallback === true ? { nameResolution: { enableMainnetL1MissFallback: true } } : {}
		}
	}), [
		ecosystemDefinition,
		profile,
		uiKit,
		enableMainnetL1MissFallback
	]);
}

//#endregion
//#region src/components/WalletConnectionUI.tsx
/**
* Component that displays wallet connection UI components
* provided by the active adapter.
*
* @example
* ```tsx
* // Basic usage
* <WalletConnectionUI />
*
* // With custom styling for the connect button
* <WalletConnectionUI
*   connectButtonProps={{ size: "lg", variant: "outline", fullWidth: true }}
* />
*
* // Customizing all components
* <WalletConnectionUI
*   connectButtonProps={{ size: "lg" }}
*   accountDisplayProps={{ size: "lg" }}
*   networkSwitcherProps={{ size: "lg" }}
* />
* ```
*/
const WalletConnectionUI = ({ className, connectButtonProps, accountDisplayProps, networkSwitcherProps }) => {
	const [isError, setIsError] = useState(false);
	const { activeNetworkConfig, activeRuntime, isRuntimeLoading } = useWalletState();
	const activeUiKit = activeRuntime?.uiKit;
	if (!!(isRuntimeLoading && activeRuntime?.networkConfig?.ecosystem && activeNetworkConfig?.ecosystem && activeRuntime.networkConfig.ecosystem !== activeNetworkConfig.ecosystem)) return null;
	const walletComponents = (() => {
		if (!activeUiKit || typeof activeUiKit.getEcosystemWalletComponents !== "function") return null;
		try {
			return activeUiKit.getEcosystemWalletComponents();
		} catch (error) {
			logger.error("WalletConnectionUI", "Error getting wallet components:", error);
			setIsError(true);
			return null;
		}
	})();
	if (!walletComponents) return null;
	const { ConnectButton, AccountDisplay, NetworkSwitcher } = walletComponents;
	if (isError) return /* @__PURE__ */ jsx("div", {
		className: cn("flex items-center gap-4", className),
		children: /* @__PURE__ */ jsx(Button, {
			variant: "destructive",
			size: "sm",
			onClick: () => window.location.reload(),
			children: "Wallet Error - Retry"
		})
	});
	return /* @__PURE__ */ jsxs("div", {
		className: cn("flex items-center gap-4", className),
		children: [
			NetworkSwitcher && /* @__PURE__ */ jsx(NetworkSwitcher, { ...networkSwitcherProps }),
			AccountDisplay && /* @__PURE__ */ jsx(AccountDisplay, { ...accountDisplayProps }),
			ConnectButton && /* @__PURE__ */ jsx(ConnectButton, { ...connectButtonProps })
		]
	});
};

//#endregion
//#region src/components/WalletConnectionHeader.tsx
/**
* Component that renders the wallet connection UI.
* Uses useWalletState to get its data.
*/
const WalletConnectionHeader = () => {
	const { isRuntimeLoading } = useWalletState();
	if (isRuntimeLoading) return /* @__PURE__ */ jsx("div", { className: "h-9 w-28 animate-pulse rounded bg-muted" });
	return /* @__PURE__ */ jsx(WalletConnectionUI, {});
};

//#endregion
//#region src/components/NetworkSwitchManager.tsx
/**
* Component that handles wallet network switching based on the selected network.
*
* This component manages the lifecycle of network switching operations,
* coordinating between the wallet's current chain state and the target network.
* It's designed to be used in any application that needs seamless wallet network switching.
*
* Features:
* - Automatically initiates network switch when mounted with a target network
* - Handles EVM chain switching gracefully
* - No-ops for non-EVM networks that don't support chain switching
* - Tracks switch attempts to prevent duplicate operations
* - Provides completion callback for parent components to handle state cleanup
*/
const NetworkSwitchManager = ({ wallet, networkCatalog, targetNetworkId, onNetworkSwitchComplete }) => {
	const isMountedRef = useRef(true);
	const [hasAttemptedSwitch, setHasAttemptedSwitch] = useState(false);
	const { isConnected, chainId: currentChainIdFromHook } = useDerivedAccountStatus();
	const { switchChain: execSwitchNetwork, isSwitching: isSwitchingNetworkViaHook, error: switchNetworkError } = useDerivedSwitchChainStatus();
	useEffect(() => {
		isMountedRef.current = true;
		logger.info("NetworkSwitchManager", `Mounted with target: ${targetNetworkId}, current attempt status: ${hasAttemptedSwitch}`);
		setHasAttemptedSwitch(false);
		return () => {
			logger.info("NetworkSwitchManager", `Unmounting, was for target: ${targetNetworkId}`);
			isMountedRef.current = false;
		};
	}, [targetNetworkId]);
	useEffect(() => {
		logger.info("NetworkSwitchManager", "State Update:", {
			target: targetNetworkId,
			walletNetwork: wallet.networkConfig.id,
			isSwitching: isSwitchingNetworkViaHook,
			hookError: !!switchNetworkError,
			canExec: !!execSwitchNetwork,
			connected: isConnected,
			walletChain: currentChainIdFromHook,
			attempted: hasAttemptedSwitch
		});
	}, [
		wallet,
		targetNetworkId,
		isSwitchingNetworkViaHook,
		switchNetworkError,
		execSwitchNetwork,
		isConnected,
		currentChainIdFromHook,
		hasAttemptedSwitch
	]);
	useEffect(() => {
		const completeOperation = (logMessage, options = { notifyComplete: true }) => {
			if (logMessage) logger.info("NetworkSwitchManager", logMessage);
			if (options.notifyComplete && isMountedRef.current && onNetworkSwitchComplete) onNetworkSwitchComplete();
			if (isMountedRef.current) setHasAttemptedSwitch(false);
		};
		if (!execSwitchNetwork) {
			completeOperation("No switchChain function available from hook. Operation halted.", { notifyComplete: false });
			return;
		}
		if (isSwitchingNetworkViaHook && hasAttemptedSwitch) {
			logger.info("NetworkSwitchManager", "Hook reports switch in progress for current attempt. Waiting...");
			return;
		}
		if (hasAttemptedSwitch && !isSwitchingNetworkViaHook) {
			logger.info("NetworkSwitchManager", "Previous switch attempt concluded. Deferring to completion effect.");
			return;
		}
		if (!networkCatalog.getNetworks().find((network) => network.id === targetNetworkId)) {
			completeOperation(`Target network ${targetNetworkId} is not present in the network catalog.`, { notifyComplete: false });
			return;
		}
		if (wallet.networkConfig.id !== targetNetworkId) {
			completeOperation(`CRITICAL: Wallet capability (${wallet.networkConfig.id}) vs Target (${targetNetworkId}) mismatch. Operation halted.`, { notifyComplete: false });
			return;
		}
		if (!isConnected) {
			completeOperation("Wallet not connected (derived status). Awaiting connection.", { notifyComplete: false });
			return;
		}
		if (!("chainId" in wallet.networkConfig)) {
			completeOperation("Network does not support chain switching (non-EVM). Operation complete (no-op).");
			return;
		}
		const targetChainToBeSwitchedTo = Number(wallet.networkConfig.chainId);
		if (currentChainIdFromHook === targetChainToBeSwitchedTo) {
			completeOperation("Already on correct chain (derived status). Operation complete.");
			return;
		}
		const performSwitchActual = () => {
			if (!isMountedRef.current || isSwitchingNetworkViaHook || hasAttemptedSwitch) {
				logger.info("NetworkSwitchManager", `Switch attempt aborted in timeout or already handled. Conditions: isSwitching: ${isSwitchingNetworkViaHook}, hasAttempted: ${hasAttemptedSwitch}`);
				return;
			}
			logger.info("NetworkSwitchManager", `Attempting switch to ${targetChainToBeSwitchedTo} via derived hook.`);
			setHasAttemptedSwitch(true);
			execSwitchNetwork({ chainId: targetChainToBeSwitchedTo });
		};
		const timeoutId = setTimeout(performSwitchActual, 100);
		return () => clearTimeout(timeoutId);
	}, [
		wallet,
		networkCatalog,
		targetNetworkId,
		execSwitchNetwork,
		isSwitchingNetworkViaHook,
		onNetworkSwitchComplete,
		isConnected,
		currentChainIdFromHook,
		hasAttemptedSwitch
	]);
	useEffect(() => {
		if (!isMountedRef.current || !execSwitchNetwork || !hasAttemptedSwitch) return;
		if (!isSwitchingNetworkViaHook) {
			let completionMessage = "Switch hook operation concluded.";
			if (switchNetworkError) {
				logger.error("NetworkSwitchManager", "Error from derived switch hook:", switchNetworkError);
				completionMessage = "Switch hook completed with error.";
			} else logger.info("NetworkSwitchManager", "Derived switch hook completed successfully.");
			if (onNetworkSwitchComplete) onNetworkSwitchComplete();
			if (isMountedRef.current) setHasAttemptedSwitch(false);
			logger.info("NetworkSwitchManager", completionMessage);
		}
	}, [
		isSwitchingNetworkViaHook,
		switchNetworkError,
		execSwitchNetwork,
		hasAttemptedSwitch,
		onNetworkSwitchComplete
	]);
	return null;
};

//#endregion
export { AnalyticsContext, AnalyticsProvider, DEFAULT_CONFIG, DEFAULT_RUNTIME_CREATION_CONFIG, NameResolutionContext, NameResolutionProvider, NetworkSwitchManager, RuntimeContext, RuntimeProvider, VERSION, WalletConnectionHeader, WalletConnectionUI, WalletStateContext, WalletStateProvider, createResolveRuntime, isMainnetL1MissFallbackEnabled, useAnalytics, useDerivedAccountStatus, useDerivedChainInfo, useDerivedConnectStatus, useDerivedDisconnect, useDerivedSwitchChainStatus, useNameResolutionContext, useNetworkNameResolver, useNetworkResolveAddress, useResolveAddress, useResolveName, useResolveRuntime, useRuntimeContext, useRuntimeNameResolver, useWalletComponents, useWalletReconnectionHandler, useWalletState };
//# sourceMappingURL=index.mjs.map